import { gql, useLazyQuery } from "@apollo/client";
import { Card, CircularProgress, Collapse, Icon } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { getUproColor } from "utils/colors";

const DAY_NAMES = ["Lunes", "Martes", "Miercoles", "Jueves", "Viernes"];
const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const normalizeTurnoKey = (value = "") =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const formatHour = (value) => {
  const text = String(value || "").trim();
  if (!text) return "--:--";
  const parts = text.split(":");
  if (parts.length < 2) return "--:--";
  return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
};

const formatOffsetText = (offset) => {
  const parsed = Number(offset || 0);
  if (!Number.isInteger(parsed) || parsed === 0) return "";
  if (parsed === -1) return " del dia anterior";
  if (parsed === 1) return " del dia siguiente";
  if (parsed < 0) return ` de ${Math.abs(parsed)} dias anteriores`;
  return ` de ${parsed} dias siguientes`;
};

const formatPedidoRange = (turno) => {
  if (!turno?.pedidoDesdeHora || !turno?.pedidoHastaHora) {
    return "Sin ventana de pedidos configurada";
  }
  return `Se puede pedir desde las ${formatHour(turno.pedidoDesdeHora)}${formatOffsetText(
    turno.pedidoDesdeOffsetDias
  )} hasta las ${formatHour(turno.pedidoHastaHora)}${formatOffsetText(turno.pedidoHastaOffsetDias)}`;
};

const getTurnoColor = (turnoNombre = "") => {
  const key = normalizeTurnoKey(turnoNombre);
  if (key.includes("mediodia")) return "#D8F3E4";
  if (key.includes("noche")) return "#FEE2E2";
  return "#E0E7FF";
};

const getMonday = (dateValue = dayjs()) => {
  const current = dayjs(dateValue).startOf("day");
  const dayIndex = current.day();
  const diff = dayIndex === 0 ? -6 : 1 - dayIndex;
  return current.add(diff, "day");
};

const getWeekLabel = (weekStart) => {
  const weekEnd = weekStart.add(4, "day");
  const startDay = weekStart.date();
  const endDay = weekEnd.date();
  const endMonth = MONTH_NAMES[weekEnd.month()];
  const year = weekEnd.year();
  return `Semana ${startDay} - ${endDay} ${endMonth} ${year}`;
};

function CalendarioMenusSemana({ token, idSede }) {
  const [weekStart, setWeekStart] = useState(() => getMonday(dayjs()));
  const [selectedDateISO, setSelectedDateISO] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [expandedSection, setExpandedSection] = useState(true);
  const [groupsByDate, setGroupsByDate] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  const [getMenusDelMes] = useLazyQuery(
    gql`
      query getMenusDelMesSemana($input: MenusDelMesInput, $token: String) {
        menusDelMes(input: $input, token: $token) {
          id
          nombre
          fechaMenu
          turno
          producto {
            id
            nombre
          }
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [getTurnosSede] = useLazyQuery(
    gql`
      query getTurnosSedeSemana($idSede: ID!, $token: String) {
        turnosSede(idSede: $idSede, token: $token) {
          id
          nombre
          codigoSistema
          horaInicio
          horaFin
          pedidoDesdeHora
          pedidoDesdeOffsetDias
          pedidoHastaHora
          pedidoHastaOffsetDias
          orden
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [getFranjasTurno] = useLazyQuery(
    gql`
      query getFranjasTurnoSemana($idTurnoSede: ID!, $idSede: ID, $fecha: String, $token: String) {
        franjasTurno(idTurnoSede: $idTurnoSede, idSede: $idSede, fecha: $fecha, token: $token) {
          id
          horaInicio
          horaFin
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const weekDays = useMemo(
    () => Array.from({ length: 5 }, (_, idx) => weekStart.add(idx, "day")),
    [weekStart]
  );

  useEffect(() => {
    if (!selectedDateISO || !weekDays.some((day) => day.format("YYYY-MM-DD") === selectedDateISO)) {
      setSelectedDateISO(weekDays[0]?.format("YYYY-MM-DD"));
    }
  }, [selectedDateISO, weekDays]);

  useEffect(() => {
    if (!token || !idSede) return undefined;

    const weekDates = weekDays.map((day) => day.format("YYYY-MM-DD"));
    const weekDatesSet = new Set(weekDates);
    const initialGroupsByDate = weekDates.reduce((acc, currentDate) => {
      acc[currentDate] = [];
      return acc;
    }, {});
    const firstWeekDate = weekDates[0];

    let mounted = true;
    setLoading(true);
    setErrorMessage("");
    setExpandedGroups({});

    const loadWeekCalendar = async () => {
      try {
        const turnosRes = await getTurnosSede({
          variables: {
            idSede: String(idSede),
            token,
          },
        });

        const turnos = (turnosRes?.data?.turnosSede || [])
          .slice()
          .sort((a, b) => Number(a?.orden || 9999) - Number(b?.orden || 9999));

        const franjaEntries = await Promise.all(
          turnos.map(async (turno) => {
            try {
              const res = await getFranjasTurno({
                variables: {
                  idTurnoSede: turno.id,
                  idSede: String(idSede),
                  fecha: firstWeekDate,
                  token,
                },
              });
              return [normalizeTurnoKey(turno.nombre), res?.data?.franjasTurno || []];
            } catch (error) {
              return [normalizeTurnoKey(turno.nombre), []];
            }
          })
        );

        const franjaMap = Object.fromEntries(franjaEntries);
        const scheduleMap = {};
        const orderMap = {};
        turnos.forEach((turno, index) => {
          const turnoKey = normalizeTurnoKey(turno?.nombre);
          const franjas = franjaMap[turnoKey] || [];
          scheduleMap[turnoKey] = {
            marco: `${formatHour(turno?.horaInicio)} - ${formatHour(turno?.horaFin)}`,
            pedido: formatPedidoRange(turno),
            comida: franjas.length
              ? franjas.map((franja) => `${formatHour(franja?.horaInicio)} - ${formatHour(franja?.horaFin)}`)
              : [],
            color: getTurnoColor(turno?.nombre),
          };
          orderMap[turnoKey] = Number(turno?.orden || index + 1);
        });

        const monthsToRequest = Array.from(
          new Set(
            weekDays.map((day) => JSON.stringify({ mes: day.month() + 1, anio: day.year() }))
          )
        ).map((item) => JSON.parse(item));

        let menuErrors = 0;
        const menusByMonth = await Promise.all(
          monthsToRequest.map(async (period) => {
            try {
              const res = await getMenusDelMes({
                variables: {
                  input: period,
                  token,
                },
              });
              return res?.data?.menusDelMes || [];
            } catch (error) {
              menuErrors += 1;
              return [];
            }
          })
        );

        const allMenus = menusByMonth.flat();
        const groupsAccumulator = weekDates.reduce((acc, dateISO) => {
          acc[dateISO] = new Map();
          return acc;
        }, {});

        allMenus.forEach((menuItem) => {
          if (!menuItem?.producto?.id) return;
          const fechaISO = dayjs(menuItem?.fechaMenu).format("YYYY-MM-DD");
          if (!weekDatesSet.has(fechaISO)) return;
          const turnoKey = normalizeTurnoKey(menuItem?.turno);
          const schedule = scheduleMap[turnoKey] || {
            marco: "--:-- - --:--",
            pedido: "Sin ventana de pedidos configurada",
            comida: [],
            color: "#F3F4F6",
          };
          const scheduleSignature = `${schedule.marco}|${schedule.pedido}|${(schedule.comida || []).join("|")}`;
          const groupKey = `${turnoKey}|${scheduleSignature}`;
          const dayGroups = groupsAccumulator[fechaISO];
          if (!dayGroups.has(groupKey)) {
            dayGroups.set(groupKey, {
              groupId: `${fechaISO}-${turnoKey}-${orderMap[turnoKey] || 9999}`,
              turno: menuItem?.turno || "Sin turno",
              marco: schedule.marco,
              pedido: schedule.pedido,
              comida: schedule.comida,
              color: schedule.color,
              order: orderMap[turnoKey] || 9999,
              menus: [],
            });
          }
          dayGroups.get(groupKey).menus.push({
            id: menuItem.id,
            nombre: menuItem?.producto?.nombre || menuItem?.nombre || "Menu",
          });
        });

        const nextGroupsByDate = {};
        weekDates.forEach((dateKey) => {
          const grouped = Array.from(groupsAccumulator[dateKey].values());
          grouped.forEach((groupItem) => {
            groupItem.menus.sort((a, b) => a.nombre.localeCompare(b.nombre));
          });
          grouped.sort((a, b) => {
            if (a.order !== b.order) return a.order - b.order;
            return a.turno.localeCompare(b.turno);
          });
          nextGroupsByDate[dateKey] = grouped;
        });

        if (!mounted) return;
        setGroupsByDate(nextGroupsByDate);
        if (menuErrors === monthsToRequest.length) {
          setErrorMessage("No se pudo cargar el listado semanal de menus para esta sesion.");
        } else {
          setErrorMessage("");
        }
      } catch (error) {
        if (!mounted) return;
        setGroupsByDate(initialGroupsByDate);
        setErrorMessage("No se pudo cargar el calendario semanal de menus.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadWeekCalendar();

    return () => {
      mounted = false;
    };
  }, [token, idSede, weekDays, getTurnosSede, getFranjasTurno, getMenusDelMes]);

  const handleToggleGroup = (key) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderTurnoGroupCard = (groupItem, dateISO) => {
    const toggleKey = `${dateISO}-${groupItem.groupId}`;
    const isExpanded = Boolean(expandedGroups[toggleKey]);
    return (
    <Card
      key={toggleKey}
      sx={{
        border: `1px solid ${groupItem.color}`,
        backgroundColor: `${groupItem.color}55`,
        boxShadow: "none",
      }}
    >
      <SoftBox p={1.2} display="flex" flexDirection="column" gap={0.2}>
        <SoftTypography variant="button" fontWeight="bold" color="dark">
          {`${groupItem.turno} ${groupItem.marco} horas`}
        </SoftTypography>
        <SoftTypography variant="caption" color="text">
          {groupItem.pedido}
        </SoftTypography>
        <SoftTypography variant="caption" color="text" fontWeight="bold" mt={0.3}>
          Horarios disponibles:
        </SoftTypography>
        {Array.isArray(groupItem.comida) && groupItem.comida.length > 0 ? (
          groupItem.comida.map((franja) => (
            <SoftTypography key={`${toggleKey}-${franja}`} variant="caption" color="text">
              {franja}
            </SoftTypography>
          ))
        ) : (
          <SoftTypography variant="caption" color="text">
            Sin horarios disponibles
          </SoftTypography>
        )}
        <SoftBox
          mt={0.7}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => handleToggleGroup(toggleKey)}
          sx={{
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <SoftTypography variant="caption" color="primary" fontWeight="bold">
            Menus del turno ({groupItem.menus.length})
          </SoftTypography>
          <Icon
            color="primary"
            sx={{
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "all 0.2s",
            }}
          >
            expand_more
          </Icon>
        </SoftBox>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <SoftBox mt={0.6} display="flex" flexDirection="column" gap={0.35}>
            {groupItem.menus.map((menu, index) => (
              <SoftTypography key={`${toggleKey}-menu-${menu.id}`} variant="caption" color="dark">
                {`${index + 1}. ${menu.nombre}`}
              </SoftTypography>
            ))}
          </SoftBox>
        </Collapse>
      </SoftBox>
    </Card>
  );
  };

  return (
    <Card sx={{ border: "1px solid rgba(0, 0, 0, 0.12)" }}>
      <SoftBox p={2}>
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ cursor: "pointer", userSelect: "none", mb: 2 }}
          onClick={() => setExpandedSection((prev) => !prev)}
        >
          <SoftBox display="flex" alignItems="center" gap={1}>
            <Icon color="primary">calendar_month</Icon>
            <SoftTypography variant="h6" fontWeight="bold">
              Menu Semanal
            </SoftTypography>
          </SoftBox>
          <Icon
            color="primary"
            sx={{
              transform: expandedSection ? "rotate(180deg)" : "rotate(0deg)",
              transition: "all 0.2s",
            }}
          >
            expand_more
          </Icon>
        </SoftBox>

        <Collapse in={expandedSection} timeout="auto" unmountOnExit>
          <SoftBox mt={1}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <SoftButton
                color="dark"
                circular
                iconOnly
                onClick={() => setWeekStart((prev) => prev.subtract(7, "day"))}
              >
                <Icon>chevron_left</Icon>
              </SoftButton>
              <SoftTypography variant="h6" fontWeight="bold" textAlign="center">
                {getWeekLabel(weekStart)}
              </SoftTypography>
              <SoftButton
                color="dark"
                circular
                iconOnly
                onClick={() => setWeekStart((prev) => prev.add(7, "day"))}
              >
                <Icon>chevron_right</Icon>
              </SoftButton>
            </SoftBox>

            <SoftTypography variant="caption" color="text" mt={2} pl={1} display="block">
              Calendario semanal de menus disponibles con horarios de turno, ventana de pedidos y franjas de comida.
            </SoftTypography>

            {loading ? (
              <SoftBox py={5} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress size={28} color="primary" />
              </SoftBox>
            ) : (
              <>
                <SoftBox
                  mt={2}
                  sx={{
                    display: { xs: "none", md: "grid" },
                    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                    gap: 1.5,
                  }}
                >
                  {weekDays.map((dayItem, index) => {
                    const dayISO = dayItem.format("YYYY-MM-DD");
                    const dayGroups = groupsByDate[dayISO] || [];
                    return (
                      <Card key={dayISO} sx={{ border: "1px solid #D7DDE5", boxShadow: "none" }}>
                        <SoftBox
                          px={1.5}
                          py={1}
                          sx={{
                            borderBottom: "1px solid #E5E7EB",
                            backgroundColor: "rgba(0,0,0,0.02)",
                          }}
                        >
                          <SoftTypography variant="caption" fontWeight="bold" color={getUproColor("uproBlue")}>
                            {DAY_NAMES[index].toUpperCase()}
                          </SoftTypography>
                          <SoftTypography variant="button" fontWeight="bold" display="block">
                            {dayItem.format("DD/MM")}
                          </SoftTypography>
                        </SoftBox>
                        <SoftBox p={1.2} display="flex" flexDirection="column" gap={1}>
                          {dayGroups.length > 0 ? (
                            dayGroups.map((groupItem) => renderTurnoGroupCard(groupItem, dayISO))
                          ) : (
                            <SoftTypography
                              variant="caption"
                              color="text"
                              textAlign="center"
                              sx={{ py: 3 }}
                            >
                              Sin menus
                            </SoftTypography>
                          )}
                        </SoftBox>
                      </Card>
                    );
                  })}
                </SoftBox>

                <SoftBox mt={2} sx={{ display: { xs: "block", md: "none" } }}>
                  <SoftBox
                    display="flex"
                    gap={1}
                    sx={{
                      overflowX: "auto",
                      pb: 1,
                      msOverflowStyle: "none",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                    }}
                  >
                    {weekDays.map((dayItem, index) => {
                      const dayISO = dayItem.format("YYYY-MM-DD");
                      const selected = selectedDateISO === dayISO;
                      return (
                        <Card
                          key={`mobile-${dayISO}`}
                          onClick={() => setSelectedDateISO(dayISO)}
                          sx={{
                            px: 1.5,
                            py: 1,
                            minWidth: "90px",
                            border: selected ? `1px solid ${getUproColor("primary")}` : "1px solid #D7DDE5",
                            backgroundColor: selected ? "rgba(29, 78, 216, 0.08)" : "#fff",
                            boxShadow: "none",
                            cursor: "pointer",
                          }}
                        >
                          <SoftTypography variant="caption" color="primary" fontWeight="bold">
                            {DAY_NAMES[index]}
                          </SoftTypography>
                          <SoftTypography variant="button" fontWeight="bold" display="block">
                            {dayItem.format("DD/MM")}
                          </SoftTypography>
                        </Card>
                      );
                    })}
                  </SoftBox>

                  <SoftBox display="flex" flexDirection="column" gap={1}>
                    {(groupsByDate[selectedDateISO] || []).length > 0 ? (
                      (groupsByDate[selectedDateISO] || []).map((groupItem) =>
                        renderTurnoGroupCard(groupItem, selectedDateISO)
                      )
                    ) : (
                      <Card sx={{ border: "1px solid #D7DDE5", boxShadow: "none" }}>
                        <SoftBox p={2}>
                          <SoftTypography variant="caption" color="text">
                            Sin menus para el dia seleccionado.
                          </SoftTypography>
                        </SoftBox>
                      </Card>
                    )}
                  </SoftBox>
                </SoftBox>
              </>
            )}

            {!!errorMessage && (
              <SoftBox mt={1.5}>
                <SoftTypography variant="caption" color="error">
                  {errorMessage}
                </SoftTypography>
              </SoftBox>
            )}
          </SoftBox>
        </Collapse>
      </SoftBox>
    </Card>
  );
}

CalendarioMenusSemana.propTypes = {
  token: PropTypes.string,
  idSede: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CalendarioMenusSemana;
